import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { crmFetch } from '../chat/api'
import type { FanData } from '../chat/useFan'
import { useChatPrefs } from './chatPrefs'
import { presenceOf } from './presence'
import { renameFanOnOnlyFans } from './rename'

export type Confirmable = 'hide' | 'restrict' | 'clear'

/**
 * What both headers (desktop and phone) do for the open fan: names, presence,
 * pin and mute, rename, and the More menu's OnlyFans writes behind a confirm
 * (chats.js hideChatThread / restrictFanUser / clearChat), one at a time with
 * a failure shown as a notice.
 */
export function useHeaderActions({ accountId, fanId, fan, nameHint, onLeave }: { accountId: string; fanId: string; fan: FanData; nameHint: string | null; onLeave: () => void }) {
  const { t } = useTranslation('chats')
  const prefs = useChatPrefs()
  const [renaming, setRenaming] = useState(false)
  const [notice, setNotice] = useState<string | null>(null)
  const p = fan.profile
  const official = p?.name || nameHint || t('header.fallbackName', { id: fanId })
  const custom = prefs.names[fanId] ?? ''
  const name = custom || official
  const presence = presenceOf(p, t)
  const pinned = prefs.pinned.has(fanId)
  const muted = prefs.muted.get(fanId) ?? prefs.listMuted.has(fanId)
  const [busy, setBusy] = useState<string | null>(null)
  const [confirming, setConfirming] = useState<Confirmable | null>(null)
  const run = async (key: string, write: () => Promise<void>, failed: string) => {
    if (busy) return
    setBusy(key)
    setNotice(null)
    try {
      await write()
    } catch (err) {
      setNotice(`${failed}: ${(err as Error).message}`)
    } finally {
      setBusy(null)
    }
  }
  const togglePin = async () => {
    setNotice(null)
    const res = await prefs.togglePin(fanId, { name: p?.name || nameHint, username: p?.username, avatar: p?.avatar })
    if (res === 'full') setNotice(t('header.pinsFull'))
    else if (res === 'failed') setNotice(t('header.pinFailed'))
    else if (res === 'unpin-failed') setNotice(t('header.unpinFailed'))
  }
  const toggleMute = () =>
    run(
      'mute',
      async () => {
        // chats.js toggleMuteChat: the gateway mutes; unmuting is OnlyFans' own DELETE.
        if (muted)
          await crmFetch('/v1/of/raw', {
            accountId,
            body: { method: 'DELETE', api_path: `/api2/v2/chats/${fanId}/mute`, referer: `https://onlyfans.com/my/chats/chat/${fanId}/`, body: null },
          })
        else await crmFetch('/v1/chats/mute', { accountId, body: { fan_id: fanId } })
        prefs.setMuted(fanId, !muted)
      },
      muted ? t('header.unmuteFailed') : t('header.muteFailed'),
    )
  /** OnlyFans' own unread (mark-as-read DELETE, as the list's swipe), then `done` (back to the list). */
  const markUnread = (done: () => void) =>
    run(
      'unread',
      async () => {
        await crmFetch('/v1/of/raw', {
          accountId,
          body: { method: 'DELETE', api_path: `/api2/v2/chats/${fanId}/mark-as-read`, referer: `https://onlyfans.com/my/chats/chat/${fanId}/`, body: null },
        })
        done()
      },
      t('phone.unreadFailed'),
    )
  const confirmed: Record<Confirmable, () => Promise<void>> = {
    hide: () =>
      run(
        'hide',
        async () => {
          await crmFetch('/v1/chats/hide', { accountId, body: { fan_id: fanId } })
          prefs.drop(fanId)
          onLeave()
        },
        t('more.hideFailed'),
      ),
    restrict: () =>
      run(
        'restrict',
        async () => {
          await crmFetch('/v1/users/restrict', { accountId, body: { user_id: fanId, fan_id: fanId } })
          setNotice(t('more.restricted'))
        },
        t('more.restrictFailed'),
      ),
    clear: () =>
      run(
        'clear',
        async () => {
          await crmFetch('/v1/chats/clear', { accountId, body: { fan_id: fanId } })
          prefs.drop(fanId)
          onLeave()
        },
        t('more.clearFailed'),
      ),
  }
  /** As fan-rename.js: OnlyFans first; the person's own name is kept even when that fails. */
  const saveName = async (next: string) => {
    try {
      await renameFanOnOnlyFans(accountId, fanId, next)
      fan.reload()
    } catch {
      setNotice(t('rename.savedLocally'))
    }
    prefs.setName(fanId, next)
  }
  return { p, official, custom, name, presence, pinned, muted, busy, notice, setNotice, renaming, setRenaming, confirming, setConfirming, togglePin, toggleMute, markUnread, confirmed, saveName }
}

export type HeaderActions = ReturnType<typeof useHeaderActions>
